import ApplicantCard from './ApplicantCard'
import { Box, Collapse, FormControl, MenuItem, Select, Stack } from '@mui/material'
import Grid from '@mui/material/Grid'
import { Applicant, GroupedApplicant, ThirdPartyData } from 'store/dashboard/types'
import { useEffect, useState } from 'react'
import SectionContainer from '../../../components/SectionContainer'
import SectionHeader from '../../../components/SectionHeader'
import OpenCloseButton from 'components/openCloseButton/OpenCloseButton'
import { useAdminQuery } from 'containers/dashboard/multisectorReport/hooks/useAdminQuery'

type Props = {
  groupedApplicants: GroupedApplicant[]
  thirdPartyDataList: ThirdPartyData[] | null
  subgroups: string[]
}

export default function SpotlightSection({ subgroups, groupedApplicants, thirdPartyDataList }: Props) {
  const isAdminView = useAdminQuery().isAdminView
  const [sectionOpened, setSectionOpened] = useState(false)
  const [filterOption, setFilterOption] = useState<string>('RAW_DATA')
  const [filterDescription, setFilterDescription] = useState<string>('')
  const getKey = (app: Applicant) => app.applicant_original_name + app.comparison_score + app.subgroup
  const [filteredApplicants, setFilteredApplicants] = useState<GroupedApplicant[]>(groupedApplicants)
  const MAX_COMPANIES_FIRST_ROUND = 70

  useEffect(() => {
    if (filterOption === 'IP_HEAVYWEIGHTS') {
      const copyGroupedApplicants = [...groupedApplicants]
      const filtered = copyGroupedApplicants
        .filter(applicant => !applicant.research)
        .sort((a, b) => b.whales_patent_in_percent - a.whales_patent_in_percent)
        .slice(0, MAX_COMPANIES_FIRST_ROUND)
        .sort((a, b) => b.whales_patents_in_categories - a.whales_patents_in_categories)
      setFilteredApplicants(filtered)
      setFilterDescription(
        `1. We filter out applicants tagged as research. 2. Then we sort by whales_patent_in_percent. 3. We take the first ${MAX_COMPANIES_FIRST_ROUND} applicants. 4. We sort them by whales_patents_in_categories. 5. We take the first 20.`,
      )
    } else if (filterOption === 'RAW_DATA') {
      setFilteredApplicants(groupedApplicants)
      setFilterDescription(`${filterOption}: No filter applied.`)
    } else {
      // ??
    }
  }, [filterOption, groupedApplicants])

  function Header() {
    return (
      <SectionHeader title="Spotlight">
        <Box display={'flex'}>
          <Stack mr={2} direction="row" alignItems="center">
            <FormControl size="small">
              <Select
                value={1}
                sx={{ color: 'primary.main' }}
                labelId="top-results-filters"
                name="top-results-filters"
                onChange={() => {}}>
                <MenuItem id="1" value={1}>
                  Owler demo data
                </MenuItem>
                <MenuItem id="2" value={2}>
                  Investor Master (locked)
                </MenuItem>
                <MenuItem id="3" value={3}>
                  CSV 1 (locked)
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ ml: 1, minWidth: 120 }}>
              <Select
                size="small"
                labelId="spotlight-filters"
                name="spotlight-filters"
                value={filterOption}
                sx={{ color: 'primary.main' }}
                onChange={event => {
                  setFilterOption(event.target.value)
                }}>
                <MenuItem value={'IP_HEAVYWEIGHTS'}>IP Heavyweights</MenuItem>
                <MenuItem value={'DIRECT_INNOVATION_THREATS'}>Direct Innovation Threats (locked)</MenuItem>
                <MenuItem value={'RAW_DATA'}>Raw data</MenuItem>
                <MenuItem value={'PURE_PLAY_INNOVATORS'}>Pure-Play Innovators (locked)</MenuItem>
                <MenuItem value={'COMMERCIAL_GIANTS'}>Commercially Strong Giants (locked)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" alignItems="center">
            <OpenCloseButton onClick={() => setSectionOpened(!sectionOpened)} opened={sectionOpened} />
          </Stack>
        </Box>
      </SectionHeader>
    )
  }
  return (
    <SectionContainer>
      {Header()}
      <Collapse in={sectionOpened}>
        <Box p={3}>
          {isAdminView && (
            <Box mb={2} fontSize={14}>
              <Box>(admin)</Box>
              <Box sx={{ fontWeight: 'bold' }}> Filter {filterOption}:</Box>
              <Box>{filterDescription}</Box>
            </Box>
          )}
          {subgroups.length === 0 && <>No sectors selected</>}
          <Grid container spacing={4} columns={{ sm: 12, md: 12, lg: 10, xl: 12 }}>
            {filteredApplicants.slice(0, 20).map((groupedApp, index) => {
              const applicantList = groupedApp.applicantBySubgroup

              const tpd = thirdPartyDataList?.filter(tpd => tpd.applicant_cleaned === applicantList[0].applicant_cleaned)[0]

              return (
                <Grid size={{ xs: 6, sm: 6, md: 4, lg: 2, xl: 2 }} key={getKey(applicantList[0])}>
                  <ApplicantCard
                    applicantList={applicantList}
                    target={index === 0}
                    multisectorSubgroups={subgroups}
                    thirdPartyData={tpd}
                  />
                </Grid>
              )
            })}
          </Grid>
        </Box>
      </Collapse>
    </SectionContainer>
  )
}
